import { useState, useEffect } from 'react'
import { useAppDispatch, useIsAuthenticated, useUser } from '@/store/hooks'
import { loginUser, clearError } from '@/store/slices/authSlice'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { sessionService } from '@/services/sessionService'
import { useAuthStore } from '@/store/authStore'

const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const isAuthenticated = useIsAuthenticated()
  const user = useUser()
  const { login: loginZustand } = useAuthStore()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user && user.role === 'member') {
      navigate('/app/dashboard', { replace: true })
    }
  }, [isAuthenticated, user, navigate])

  // Clear any existing errors on component mount
  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      console.log('Starting member login...')
      const resultAction = await dispatch(loginUser({
        email,
        password,
        loginType: 'member'
      }))

      if (loginUser.fulfilled.match(resultAction)) {
        const { user, sessionId, token, refreshToken } = resultAction.payload
        console.log('Login successful, user:', user)

        // Check if user has member privileges
        if (user && user.role === 'member') {
          console.log('Member user authenticated, starting session and navigating...')

          // Sync Zustand store with Redux state
          try {
            await loginZustand(email, password, 'member')
          } catch (err) {
            // If Zustand login fails, manually set the state
            useAuthStore.setState({
              user,
              token,
              refreshToken,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }

          // Start session tracking
          if (sessionId) {
            sessionService.startSession(sessionId)
          }

          // Navigate to dashboard with replace to prevent going back to login
          navigate('/app/dashboard', { replace: true })
        } else {
          console.error('User role not authorized for member access:', user?.role)
          setError('Access denied. This login is for members only.')
        }
      } else {
        // Handle login failure
        const errorMessage = resultAction.payload as string
        console.error('Member login failed:', errorMessage)
        setError(errorMessage || 'Login failed. Please check your credentials.')
      }
    } catch (error: any) {
      console.error('Member login error:', error)
      setError(error.message || 'Login failed. Please check your credentials.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Member Login
            </h2>
            <p className="text-gray-600 mb-6">
              Access your RWA member account
            </p>
          </div>

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {(location.state as { passwordReset?: boolean } | null)?.passwordReset && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
                Password updated successfully. You can now log in.
              </div>
            )}
            {error && (
              <div data-testid="error-message" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="email-input"
                aria-label="Email Address"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="member@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="password-input"
                aria-label="Password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your password"
              />
            </div>

            <div className="text-right text-sm">
              <Link to="/forgot-password?type=member" className="font-medium text-blue-600 hover:text-blue-500">
                Forgot your password?
              </Link>
            </div>

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={isLoading}
              data-testid="login-button"
              aria-label="Sign in to Account"
            >
              {isLoading ? 'Signing in...' : 'Sign in to Account'}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-2">
            <Link 
              to="/" 
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              ← Back to Home
            </Link>
            <div className="text-xs text-gray-500">
              For staff login, use <Link to="/user-login" className="text-blue-600 hover:text-blue-800">User Login</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
